// src/components/blog/BlogBody.tsx
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { BlogBlock, BlogInline } from '../../types/blogTypes';
import { parseBlogBody, parseInline } from '../../utils/blogMarkdown';

const Article = styled.div`
  color: #3f4c5a;
  font-size: 18px;
  line-height: 1.8;

  p {
    font-size: 18px;
    color: #3f4c5a;
    margin: 0 0 22px;
  }

  h2 {
    font-size: clamp(26px, 3vw, 34px);
    font-weight: 700;
    color: #1f2b3a;
    margin: 48px 0 16px;
  }

  h3 {
    font-size: clamp(20px, 2.2vw, 24px);
    color: #1f2b3a;
    margin: 32px 0 12px;
  }

  ul,
  ol {
    margin: 0 0 24px;
    padding-left: 24px;
  }

  li {
    margin-bottom: 10px;
    padding-left: 4px;
  }

  strong {
    color: #1f2b3a;
    font-weight: 600;
  }

  a {
    font-weight: 600;
    border-bottom: 1px solid rgba(31, 43, 58, 0.3);
  }

  a:hover {
    border-bottom-color: rgba(15, 28, 45, 0.7);
  }
`;

const PromptQuote = styled.blockquote`
  margin: 0 0 24px;
  padding: 18px 22px;
  border-left: 4px solid #817ffd;
  border-radius: 0 14px 14px 0;
  background: rgba(129, 127, 253, 0.1);
  color: #1f2b3a;
  font-size: 17px;
  line-height: 1.7;
`;

const renderInline = (inline: BlogInline, key: number): React.ReactNode => {
  switch (inline.type) {
    case 'bold':
      return <strong key={key}>{inline.text}</strong>;
    case 'italic':
      return <em key={key}>{inline.text}</em>;
    case 'link':
      return inline.href.startsWith('/') ? (
        <Link key={key} to={inline.href}>
          {inline.text}
        </Link>
      ) : (
        <a key={key} href={inline.href} target="_blank" rel="noopener noreferrer">
          {inline.text}
        </a>
      );
    default:
      return <React.Fragment key={key}>{inline.text}</React.Fragment>;
  }
};

const renderText = (text: string): React.ReactNode[] => parseInline(text).map(renderInline);

const renderBlock = (block: BlogBlock, key: number): React.ReactNode => {
  switch (block.type) {
    case 'h2':
      return <h2 key={key}>{renderText(block.text)}</h2>;
    case 'h3':
      return <h3 key={key}>{renderText(block.text)}</h3>;
    case 'quote':
      return <PromptQuote key={key}>{renderText(block.text)}</PromptQuote>;
    case 'ul':
      return (
        <ul key={key}>
          {block.items.map((item, index) => (
            <li key={index}>{renderText(item)}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol key={key}>
          {block.items.map((item, index) => (
            <li key={index}>{renderText(item)}</li>
          ))}
        </ol>
      );
    default:
      return <p key={key}>{renderText(block.text)}</p>;
  }
};

interface BlogBodyProps {
  body: string;
}

const BlogBody: React.FC<BlogBodyProps> = ({ body }) => (
  <Article>{parseBlogBody(body).map(renderBlock)}</Article>
);

export default React.memo(BlogBody);
